import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SliceCaseReducers } from '@reduxjs/toolkit/src/createSlice';
import { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import SiteApi from '../../Api/SiteApi/SiteApi';
import Money from '../../Api/Types/Money';

import { getUserCurrency } from '../../utils/currency';
import { formatCurrency } from '../../utils/formatCurrency';

import FetchInterface from './FetchInterface';
import LoadingInterface from './LoadingInterface';

const fetchUserBalance = createAsyncThunk(
    'user/fetchBalance',
    async (): Promise<Money | null> => {
        try {
            return (await SiteApi.get('account/balance')).data;
        } catch (e) {
            if (e.response && e.response.status === 401) {
                return null;
            }
            throw e;
        }
    }
);

// TODO: fill this with more data in future
interface UserInterface {
    balance: Money | null;
}

interface UserSliceInterface extends FetchInterface, UserInterface {}

interface UserStateSelector {
    user: UserSliceInterface;
}

const currency = getUserCurrency();

const UserSlice = createSlice<UserSliceInterface, SliceCaseReducers<UserSliceInterface>>({
    name: 'user',
    initialState: {
        isLoading: false,
        isFetched: false,
        balance: {
            amount: 0,
            currency,
            formatted: formatCurrency(0, 2, currency),
        },
    },
    reducers: {
        setBalance: (state, action: PayloadAction<Money>) => {
            state.balance = action.payload;
            state.isFetched = true;
            state.isLoading = false;
        },
        // This is not totally ok, because we don't need formatted field
        changeBalanceBy: (state, action: PayloadAction<Money>) => {
            if (state.balance.currency !== action.payload.currency) {
                throw new Error('Cannot change use balance. Multi-currency feature is not ready yet.');
            }

            const amount = state.balance.amount + action.payload.amount;
            state.balance = {
                amount,
                currency: state.balance.currency,
                // TODO: This is temporary solution and it's invalid in log term
                formatted: formatCurrency(amount / 100, 2, state.balance.currency),
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchUserBalance.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchUserBalance.fulfilled, (state, action) => {
            state.balance = action.payload;
            state.isLoading = false;
            state.isFetched = true;
        });
    },
});

export const { changeBalanceBy, setBalance } = UserSlice.actions;
export default UserSlice.reducer;

export const useUserBalance = function (): LoadingInterface & UserInterface & { isFetched: boolean } {
    const dispatch = useDispatch();

    const { balance, isLoading, isFetched } = useSelector((state: UserStateSelector) => state.user);

    useEffect(() => {
        if (!isFetched && !isLoading) {
            dispatch(fetchUserBalance());
        }
    }, [dispatch]);

    return {
        isLoading,
        isFetched,
        balance,
    };
};
